import type { FloorEdgeOverridesTable } from "building-types/floors/floorAssemblyTypes";
import type {
  FloorSplitData,
  SplitLine,
  SplitLineEdgeOverride,
  SplitLineEdgeOverridesTable,
} from "building-types/floors/floorSplitDataTypes";
import { arrayShift } from "common/utils/arrayShift";
import { offsetPolyLine2VariableStep } from "geom/offset";
import { polyLine2EdgeCount } from "geom/polyLine2";
import {
  polyLine2Copy,
  polyLine2IsClosed,
  polyLine2Move,
  polyLine2ReflectOverYAxis,
  polyLine2Reverse,
  polyLine2SetCW,
  polyLine2Shift,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";

/** Prefix for transition edge IDs created when offsetting floor edges. These edges should not have labels. */
export const FLOOR_TRANSITION_EDGE_PREFIX = "floor-transition-";

/**
 * Checks if an edge ID represents a transition edge (created when offsetting floor edges).
 */
export function isFloorTransitionEdge(edgeId: string): boolean {
  return edgeId.startsWith(FLOOR_TRANSITION_EDGE_PREFIX);
}

/**
 * Extracts the source edge ID from a floor transition edge ID.
 * Transition edges inherit their properties (assembly, datums, framing config, etc.) from the source edge.
 * Format: "floor-transition-{sourceEdgeId}-{counter}"
 * Returns undefined if the edge ID is not a transition edge.
 */
export function getFloorTransitionEdgeSourceId(edgeId: string): string | undefined {
  if (!isFloorTransitionEdge(edgeId)) {
    return undefined;
  }
  // Format: "floor-transition-{sourceEdgeId}-{counter}"
  // sourceEdgeId is everything between the prefix and the last "-{counter}" segment
  const rest = edgeId.slice(FLOOR_TRANSITION_EDGE_PREFIX.length);
  const lastDash = rest.lastIndexOf("-");
  if (lastDash > 0) {
    return rest.slice(0, lastDash);
  }
  return rest || undefined;
}

/**
 * Computes the 1-based label index for an edge at `edgeIndex` within `edgeIds`,
 * skipping transition edges. Returns 0 if `edgeIndex` itself is a transition edge.
 */
export function computeEdgeLabelIndex(edgeIds: string[], edgeIndex: number): number {
  if (edgeIndex < edgeIds.length && isFloorTransitionEdge(edgeIds[edgeIndex])) {
    return 0;
  }
  let labelIndex = 0;
  for (let i = 0; i <= edgeIndex && i < edgeIds.length; i++) {
    if (!isFloorTransitionEdge(edgeIds[i])) {
      labelIndex++;
    }
  }
  return labelIndex;
}

export function mirrorFloorPerimeter(perimeter: PolyLine2, mirrorTranslationX: number): PolyLine2 {
  const mirroredPerimeter = polyLine2ReflectOverYAxis(polyLine2Copy(perimeter));
  polyLine2Shift(polyLine2Reverse(mirroredPerimeter), 1);
  polyLine2Move(mirroredPerimeter, mirrorTranslationX, 0);
  return mirroredPerimeter;
}

export function mirrorFloorEdgeIds(edgeIds: string[]): string[] {
  return arrayShift(edgeIds.slice().reverse(), 1);
}

/**
 * Mirrors combined edge IDs for multiple polylines (perimeters or openings).
 * Each polyline's edge IDs are reversed + shifted independently within the
 * combined array, preventing edge IDs from mixing across polyline boundaries.
 * For a single polyline this is equivalent to `mirrorFloorEdgeIds`.
 */
export function mirrorFloorEdgeIdsMulti(edgeIds: string[], edgeCounts: number[]): string[] {
  const result: string[] = [];
  let offset = 0;
  for (const count of edgeCounts) {
    const slice = edgeIds.slice(offset, offset + count);
    result.push(...mirrorFloorEdgeIds(slice));
    offset += count;
  }
  return result;
}

export function mirrorFloorOpening(opening: PolyLine2, mirrorTranslationX: number): PolyLine2 {
  let openingCopy = polyLine2SetCW(polyLine2Copy(opening));
  openingCopy = polyLine2ReflectOverYAxis(openingCopy);
  if (polyLine2IsClosed(openingCopy)) {
    polyLine2Shift(polyLine2Reverse(openingCopy), 1);
  } else {
    polyLine2Reverse(openingCopy);
  }
  polyLine2Move(openingCopy, mirrorTranslationX, 0);
  return openingCopy;
}

export interface OpeningEdgeOverrideResult {
  openings: PolyLine2[];
  openingEdgeIds: string[];
  openingEdgeCounts: number[];
}

/**
 * Applies opening edge overrides (offsets) to an array of opening polylines.
 * Overrides are looked up by stable edge ID from `openingEdgeIds`.
 * Returns updated openings, edge IDs (including transition edges), and edge counts.
 */
export function applyOpeningEdgeOverrides(
  openings: PolyLine2[],
  openingEdgeCounts: number[] | undefined,
  openingEdgeIds: string[] | undefined,
  floorOpeningEdgeOverrides: FloorEdgeOverridesTable | undefined,
): OpeningEdgeOverrideResult {
  const fallbackEdgeCounts = openings.map((o) => polyLine2EdgeCount(o));
  const effectiveEdgeCounts = openingEdgeCounts ?? fallbackEdgeCounts;

  if (openings.length === 0) {
    return { openings, openingEdgeIds: [], openingEdgeCounts: [] };
  }

  if (!floorOpeningEdgeOverrides || !openingEdgeIds || openingEdgeIds.length === 0) {
    return {
      openings,
      openingEdgeIds: openingEdgeIds ?? [],
      openingEdgeCounts: [...effectiveEdgeCounts],
    };
  }

  const resultOpenings: PolyLine2[] = [];
  const resultEdgeIds: string[] = [];
  const resultEdgeCounts: number[] = [];
  let globalOffset = 0;

  for (let openingIndex = 0; openingIndex < openings.length; openingIndex++) {
    const opening = openings[openingIndex];
    const edgeCount = effectiveEdgeCounts[openingIndex] ?? polyLine2EdgeCount(opening);
    const localEdgeIds: string[] = [];
    const offsets: number[] = [];
    for (let localEdge = 0; localEdge < edgeCount; localEdge++) {
      const edgeId = openingEdgeIds[globalOffset + localEdge];
      localEdgeIds.push(edgeId ?? `unknown-${localEdge}`);
      offsets.push(edgeId !== undefined ? -(floorOpeningEdgeOverrides[edgeId]?.offset ?? 0) : 0);
    }
    globalOffset += edgeCount;

    if (offsets.some((v) => v !== 0)) {
      const { pl, edgeMapping } = offsetPolyLine2VariableStep(polyLine2Copy(opening), offsets, true);
      const updatedEdgeIds = buildTransitionEdgeIds(edgeMapping, localEdgeIds, offsets);
      resultOpenings.push(pl);
      resultEdgeIds.push(...updatedEdgeIds);
      resultEdgeCounts.push(updatedEdgeIds.length);
    } else {
      resultOpenings.push(opening);
      resultEdgeIds.push(...localEdgeIds);
      resultEdgeCounts.push(localEdgeIds.length);
    }
  }

  return { openings: resultOpenings, openingEdgeIds: resultEdgeIds, openingEdgeCounts: resultEdgeCounts };
}

/**
 * Applies floor edge overrides (offsets) to a perimeter. Overrides are looked up
 * by stable edge ID from the `edgeIds` array.
 * Uses offsetPolyLine2VariableStep for sharp transitions and generates transition
 * edge IDs for any synthetic edges created between edges with different offsets.
 */
export function floorPerimeterInfoToUse(
  perimeter: PolyLine2,
  edgeIds: string[],
  floorEdgeOverrides: FloorEdgeOverridesTable | undefined,
  offset?: number,
  applyOffset: boolean = true,
): { perimeter: PolyLine2; edgeIds: string[] } {
  let perimeterToUse = polyLine2Copy(perimeter);
  let edgeIdsToUse = [...edgeIds];

  if (perimeterToUse && edgeIdsToUse.length > 0 && applyOffset) {
    const offsets: number[] = edgeIdsToUse.map((edgeId) => floorEdgeOverrides?.[edgeId]?.offset ?? offset ?? 0);
    if (offsets.some((v) => v !== 0)) {
      const { pl, edgeMapping } = offsetPolyLine2VariableStep(perimeterToUse, offsets, true);
      perimeterToUse = pl;
      edgeIdsToUse = buildTransitionEdgeIds(edgeMapping, edgeIdsToUse, offsets);
    }
  }
  return { perimeter: perimeterToUse, edgeIds: edgeIdsToUse };
}

/**
 * Maps an edgeMapping from offsetPolyLine2VariableStep to updated edge IDs.
 * Original edges keep their IDs; transition edges (undefined in the mapping) get
 * synthetic IDs in the format "floor-transition-{sourceEdgeId}-{counter}".
 * The source edge is the nearest adjacent original edge with a non-zero offset.
 */
function buildTransitionEdgeIds(
  edgeMapping: (number | undefined)[],
  originalEdgeIds: string[],
  offsets: number[],
): string[] {
  let transitionCounter = 0;
  return edgeMapping.map((originalIndex, newIndex) => {
    if (originalIndex !== undefined) {
      return originalEdgeIds[originalIndex];
    }

    let sourceEdgeId: string | undefined;

    // Look backward for the nearest original edge with a non-zero offset
    for (let i = newIndex - 1; i >= 0; i--) {
      const prevOriginal = edgeMapping[i];
      if (prevOriginal !== undefined && offsets[prevOriginal] !== 0) {
        sourceEdgeId = originalEdgeIds[prevOriginal];
        break;
      }
    }

    // Look forward if no offset edge found before
    if (!sourceEdgeId) {
      for (let i = newIndex + 1; i < edgeMapping.length; i++) {
        const nextOriginal = edgeMapping[i];
        if (nextOriginal !== undefined && offsets[nextOriginal] !== 0) {
          sourceEdgeId = originalEdgeIds[nextOriginal];
          break;
        }
      }
    }

    // Fallback to the nearest original edge regardless of offset
    if (!sourceEdgeId) {
      for (let i = newIndex - 1; i >= 0; i--) {
        const mappedIndex = edgeMapping[i];
        if (mappedIndex !== undefined) {
          sourceEdgeId = originalEdgeIds[mappedIndex];
          break;
        }
      }
    }

    return `${FLOOR_TRANSITION_EDGE_PREFIX}${sourceEdgeId ?? "unknown"}-${transitionCounter++}`;
  });
}

/**
 * Mirrors a Vec2 interior point by reflecting its x-coordinate and translating.
 * The y-coordinate is preserved.
 */
export function mirrorInteriorPoint(point: Vec2, mirrorTranslationX: number): Vec2 {
  return [-point[0] + mirrorTranslationX, point[1]];
}

/**
 * Mirrors split data for floor generation. Split lines are open polylines,
 * so the transformation is: reflect over Y-axis → reverse → translate by mirrorTranslationX.
 * Edge IDs are reversed (no cyclic shift for open polylines) and edge overrides
 * are remapped to match the reversed edge IDs. Bay entries pass through unchanged
 * (bays are matched separately via interiorPoint containment).
 */
export function mirrorSplitData(splitData: FloorSplitData, mirrorTranslationX: number): FloorSplitData {
  const splitLines = splitData.splitLines;
  if (!splitLines || Object.keys(splitLines).length === 0) {
    return splitData;
  }

  const mirroredSplitLines: Record<string, SplitLine> = {};
  for (const [slId, sl] of Object.entries(splitLines)) {
    const mirroredPolyline = polyLine2ReflectOverYAxis(polyLine2Copy(sl.polyline));
    polyLine2Reverse(mirroredPolyline);
    polyLine2Move(mirroredPolyline, mirrorTranslationX, 0);

    const mirroredEdgeIds = sl.edgeIds.slice().reverse();

    let mirroredEdgeOverrides: SplitLineEdgeOverridesTable | undefined;
    if (sl.edgeOverrides) {
      const remapped: Record<string, SplitLineEdgeOverride> = {};
      for (const [edgeId, override] of Object.entries(sl.edgeOverrides)) {
        const originalIndex = sl.edgeIds.indexOf(edgeId);
        if (originalIndex !== -1) {
          const newEdgeId = mirroredEdgeIds[sl.edgeIds.length - 1 - originalIndex];
          if (newEdgeId !== undefined) {
            remapped[newEdgeId] = override;
          }
        }
      }
      mirroredEdgeOverrides = Object.keys(remapped).length > 0 ? remapped : undefined;
    }

    mirroredSplitLines[slId] = {
      ...sl,
      polyline: mirroredPolyline,
      edgeIds: mirroredEdgeIds,
      edgeOverrides: mirroredEdgeOverrides,
    };
  }

  return { ...splitData, splitLines: mirroredSplitLines };
}
